/*
 * Copyright (C) 2017 Spanning Cloud Apps LLC.  All rights reserved.
 */

import './results.tpl.html';

angular
  .module('metadataCompare.results', ['shared.metadataCompareDiffModal', 'services.bulkrestoredata', 'services.util'])
  /**
   * Controller for the results.tpl.html template.
   */
  .controller(
    'MetadataCompareResultsViewCtrl',
    function (
      $scope,
      $rootScope,
      $state,
      $stateParams,
      $filter,
      batchUtils,
      httpMetadataRestore,
      BulkRestoreDataFactory,
      BulkRestoreType,
      ALERT_EVENTS,
      BATCH_RANGE,
      BULK_RESTORE_DEST_TYPES,
      BULK_RESTORE_TYPES,
      VIEW_STATES,
      ERROR_TYPES
    ) {
      var i18n = $filter('i18n');
      var batchId = parseInt($stateParams.batchId);
      $scope.batchId = batchId;
      $rootScope.showLoadingMask = true;
      $scope.showMetadataWithDifference = false;

      $scope.expired = false;
      $scope.runByText = '';

      // need to retrieve that batch
      batchUtils
        .loadBatch(batchId)
        .then(
          function (batch) {
            $scope.batch = batch;
            $scope.rightBatchId = batch.rightBatchId;
            $scope.typeCounts = batch.typeCounts;
            $scope.storedTypeCounts = $scope.typeCounts;

            if (batch.userEmail && batch.date) {
              $scope.runByText = i18n('RUN_BY', [batch.userEmail, formatDate(batch.date)]);
            }

            $scope.sourceHeader = i18n('COMPONENTS_FROM_BACKUP', [formatDate(batch.leftBatchDate)]);

            if (batch.leftSfOrgId !== batch.rightSfOrgId) {
              $scope.targetHeader = i18n('CURRENT_COMPONENTS_AS_OF_CROSS_ORG', [
                batch.rightOrgName,
                formatDate(batch.date)
              ]);
            } else if (batch.rightBatchDate !== batch.date) {
              $scope.targetHeader = i18n('COMPONENTS_FROM_BACKUP', [formatDate(batch.rightBatchDate)]);
            } else {
              $scope.targetHeader = i18n('CURRENT_COMPONENTS_AS_OF', [formatDate(batch.date)]);
            }

            // add left and right SfOrg id's to scope, so they can be used in MetadataCompareDetailsRowCtrl
            $scope.leftSfOrgId = batch.leftSfOrgId;
            $scope.rightSfOrgId = batch.rightSfOrgId;

            // if the batch date is within 14 days of today, its not expired
            if (moment(batch.date).add(14, 'days').isAfter(moment())) {
              $scope.typeCounts = batch.typeCounts;
              $scope.expired = false;
            } else {
              $scope.expired = true;
              $scope.expiredMessage = i18n('METADATA_COMPARISON_EXPIRED');
            }

            $scope.isFailure = batch.status === 'FAILURE';

            if ($scope.isFailure) {
              for (const event of batch.events) {
                if (ERROR_TYPES[event.code].type === 'SALESFORCE_INACTIVE_USER') {
                  $scope.isInactiveUser = true;
                  break;
                }
              }
            }

            if ($scope.batch.status === 'CANCELLED') {
              for (const event of batch.events) {
                if (ERROR_TYPES[event.code].type === 'BATCH_CANCELLED') {
                  $scope.batchCancellationMessage = event.message;
                  break;
                }
              }
            }
          },
          function () {
            $rootScope.$emit(ALERT_EVENTS.ERROR.eventName, i18n('INTERNAL_ERROR_DESC'));
          }
        )
        .then(function () {
          httpMetadataRestore.getRestorableComponentTypes().then(function (types) {
            $scope.restorableComponentTypes = types;
          });
        })
        .finally(function () {
          $rootScope.showLoadingMask = false;
        });

      /**
       * Function to export results.
       */
      $scope.exportMetadataCompareResults = function () {
        //TODO: implement and test
      };

      /**
       * Determines whether the metadata on the results page can be restored. This requires:
       *
       * 1) The compare is against the live data
       * 2) The compare was successful and has not expired
       * 3) The compare includes restorable metadata
       * 4) There are changes or deletions (items only in the left column). Via restore, we can't delete items that
       * were recently added (items only in the right column).
       *
       * @returns {boolean} - true if the displayed items can be restored.
       */
      $scope.canRestore = function () {
        // Do the quick checks first. Live data is indicated by right batch id unbounded
        if (
          $scope.isFailure ||
          $scope.expired ||
          $scope.rightBatchId !== BATCH_RANGE.UNBOUNDED || // rightBatchId and leftBatchId are obtained from the SQS message of the Metadata Compare batch.
          $scope.leftBatchId === '' // rightBatchId and leftBatchId can return null if an error occurred in the Metadata Compare process
        ) {
          return false;
        }

        // Get the number and types of restorable items changed or deleted
        var restorableTypeCounts = _.filter($scope.typeCounts, function (counts, name) {
          return isRestorable(name) && (counts.diff > 0 || counts.leftOnly > 0);
        });

        return _.size(restorableTypeCounts) > 0;
      };

      /**
       * Determines whether a metadata component type is restorable.
       *
       * @param {string} componentType - The metadata component type to be checked
       * @returns {boolean} true if the component type is restorable
       */
      function isRestorable(componentType) {
        return _.contains($scope.restorableComponentTypes, componentType);
      }

      /**
       * Transitions to the restore page and displays the items from this page that can be restored.
       */
      $scope.restore = function () {
        var isCrossOrg = $scope.batch.leftSfOrgId !== $scope.batch.rightSfOrgId;

        // Set up the bulk restore data so the restore wizard knows where to start
        var bulkRestoreData = BulkRestoreDataFactory.generate(BulkRestoreType.METADATA_COMPARE, {
          destType: isCrossOrg ? BULK_RESTORE_DEST_TYPES.CROSS_ORG : BULK_RESTORE_DEST_TYPES.SAME_ORG,
          sourceOrgInfo: {
            sfId: $scope.batch.leftSfOrgId
          },
          destOrgInfo: isCrossOrg
            ? {
                name: $scope.batch.rightOrgName,
                sfId: $scope.batch.rightSfOrgId
              }
            : undefined,
          backupBatchId: $scope.batch.leftBatchId,
          backupBatchDate: $scope.batch.leftBatchDate,
          compareBatchId: $scope.batchId,
          compareBatchDate: $scope.batch.date
        });

        $state.go(VIEW_STATES.BULK_RESTORE_ENTER, { bulkRestoreData: bulkRestoreData });
      };

      /**
       * Helper function to format a date.
       *
       * @param {Date} date - the date to be formatted
       * @returns {string} - the formatt4ed date string
       */
      function formatDate(date) {
        return moment(date).format('MMM D, YYYY h:mm A');
      }

      $scope.toggleShowMetadataWithDifference = function () {
        if ($scope.showMetadataWithDifference) {
          $scope.typeCounts = $scope.storedTypeCounts;
        } else {
          let newTypes = {};
          for (let type in $scope.typeCounts) {
            if (
              $scope.typeCounts[type].diff > 0 ||
              $scope.typeCounts[type].leftOnly > 0 ||
              $scope.typeCounts[type].rightOnly > 0
            ) {
              newTypes[type] = $scope.typeCounts[type];
            }
          }
          $scope.typeCounts = newTypes;
        }
        $scope.showMetadataWithDifference = !$scope.showMetadataWithDifference;
      };
    }
  )
  .controller(
    'MetadataCompareDetailsRowCtrl',
    function ($scope, $uibModal, $filter, httpMetadataCompare, UtilService, METADATA_COMPARE_STATE) {
      var currPageNum = 1,
        diffPageNum = 1;
      $scope.showMessages = false;
      $scope.allMetadataFiles = [];
      $scope.diffMetadataFiles = [];
      $scope.metadataFiles = [];
      $scope.total = 0;
      $scope.showLoadingMask = false;
      $scope.showOnlyWithDifference = false;

      $scope.METADATA_COMPARE_STATE = METADATA_COMPARE_STATE;

      /**
       * Returns boolean whether to show up or down arrow for the message container.
       * @returns {string} - The class to use.
       */
      $scope.isExpanded = function () {
        var headerClass = '';
        if ($scope.showMessages) {
          headerClass = 'sbsf-log-header-asc sca-icon-down-black';
        } else {
          headerClass = 'sbsf-log-header-desc sca-icon-down-black';
        }

        if (!$scope.countObj.rightCount) {
          headerClass += ' hidden';
        }

        return headerClass;
      };

      /**
       * Function used to get next 10 metadata component files for type
       */
      $scope.getNextRecords = function () {
        $scope.showLoadingMask = true;

        if ($scope.showOnlyWithDifference) {
          diffPageNum++;
        } else {
          currPageNum++;
        }
        const pageNum = $scope.showOnlyWithDifference ? diffPageNum : currPageNum;

        httpMetadataCompare
          .getMetadataCompareFilesByType($scope.batchId, $scope.metadataType, pageNum, $scope.showOnlyWithDifference)
          .then(
            function (result) {
              $scope.total = result.count;
              $scope.metadataFiles = $scope.showOnlyWithDifference
                ? $scope.diffMetadataFiles.concat(result.rows)
                : $scope.allMetadataFiles.concat(result.rows);
              $scope.diffMetadataFiles = $scope.showOnlyWithDifference
                ? $scope.diffMetadataFiles.concat(result.rows)
                : $scope.diffMetadataFiles;
              $scope.allMetadataFiles = !$scope.showOnlyWithDifference
                ? $scope.allMetadataFiles.concat(result.rows)
                : $scope.allMetadataFiles;

              $scope.showLoadingMask = false;
            },
            function () {
              $scope.showLoadingMask = false;
            }
          );
      };

      /**
       * Function to show the number and type of changes
       *
       * @param {Object} countObj - The count object
       * @returns {String}
       */
      $scope.modifiedCounts = function (countObj) {
        var i18n = $filter('i18n');
        var counts = [];

        if (countObj.diff) {
          counts.push(i18n('NUM_CHANGED', [countObj.diff]));
        }
        if (countObj.leftOnly) {
          counts.push(i18n('NUM_DELETED', [countObj.leftOnly]));
        }
        if (countObj.rightOnly) {
          counts.push(i18n('NUM_ADDED', [countObj.rightOnly]));
        }
        if (counts.length < 1) {
          counts.push(i18n('NO_DIFF'));
        }

        return counts.join(', ');
      };

      /**
       * Function used to hide the message container.
       */
      $scope.hideMessage = function () {
        if (!$scope.countObj.rightCount) {
          return;
        }

        $scope.showMessages = !$scope.showMessages;

        // lazy load the records
        if ($scope.showMessages && $scope.metadataFiles.length === 0) {
          $scope.showLoadingMask = true;

          httpMetadataCompare
            .getMetadataCompareFilesByType($scope.batchId, $scope.metadataType, null, $scope.showOnlyWithDifference)
            .then(
              function (result) {
                $scope.total = result.count;
                $scope.metadataFiles = result.rows;
                if ($scope.showOnlyWithDifference) {
                  $scope.diffMetadataFiles = result.rows;
                } else {
                  $scope.allMetadataFiles = result.rows;
                }

                $scope.showLoadingMask = false;
              },
              function () {
                $scope.showLoadingMask = false;
              }
            );
        }
      };

      /**
       * Function to show the diff in a modal.
       * @param {Object} fileObj - The file object containing S3 paths and file name.
       */
      $scope.openDiffModal = function (fileObj) {
        $uibModal.open({
          templateUrl: 'shared/modal/metadataCompareDiffModal.tpl.html',
          controller: 'MetadataDiffModalCtrl',
          windowClass: 'sbsf-metadata-compare-modal-window',
          backdrop: 'static',
          resolve: {
            fileInfo: function () {
              // add the leftSfOrgId and rightSfOrgId and headers (since we dont have batch in modal)
              fileObj.leftSfOrgId = $scope.$parent.leftSfOrgId;
              fileObj.rightSfOrgId = $scope.$parent.rightSfOrgId;
              fileObj.leftHeader = $scope.$parent.sourceHeader;
              fileObj.rightHeader = $scope.$parent.targetHeader;
              return fileObj;
            }
          }
        });

        // this is a total hack to snap to the top of the screen when the modal is opened.
        UtilService.scrollToElement('sbsf-md-results-top');
      };

      $scope.toggleShowWithDifference = function () {
        const pageNum = $scope.showOnlyWithDifference ? currPageNum : diffPageNum;
        $scope.showOnlyWithDifference = !$scope.showOnlyWithDifference;

        $scope.showLoadingMask = true;
        httpMetadataCompare
          .getMetadataCompareFilesByType($scope.batchId, $scope.metadataType, pageNum, $scope.showOnlyWithDifference)
          .then(
            function (result) {
              if ($scope.showOnlyWithDifference) {
                $scope.metadataFiles = $scope.diffMetadataFiles;
              } else {
                $scope.metadataFiles = $scope.allMetadataFiles;
              }
              $scope.total = result.count;

              if ($scope.metadataFiles.length === 0) {
                $scope.metadataFiles = result.rows;
                if ($scope.showOnlyWithDifference) {
                  $scope.diffMetadataFiles = result.rows;
                } else {
                  $scope.allMetadataFiles = result.rows;
                }
              }

              $scope.showLoadingMask = false;
            },
            function () {
              $scope.showLoadingMask = false;
            }
          );
      };
    }
  )
  /**
   * The directive for the metadata compare details row.
   */
  .directive('metadataCompareDetailsRow', function () {
    return {
      templateUrl: 'metadataCompare/resultsView/metadataCompareDetailsRow.tpl.html',
      scope: {
        batchId: '=',
        metadataType: '=',
        countObj: '='
      },
      controller: 'MetadataCompareDetailsRowCtrl'
    };
  });
